// Shared filter state for the property browser UI (map + list + filter sheet).
// The UI keeps everything as strings/arrays for controlled inputs; toApiFilters
// maps it onto the wire shape the jlApi fetchers serialize.

import type { ListingFilters } from "../../lib/jlApi";

export interface Filters {
  search: string;
  priceMin: string;
  priceMax: string;
  bed: string;
  bath: string;
  types: string[];
  status: string;
}

export const DEFAULT_FILTERS: Filters = {
  search: "",
  priceMin: "",
  priceMax: "",
  bed: "",
  bath: "",
  types: [],
  status: "Active",
};

// Map the UI filter state onto the UI-agnostic ListingFilters shape.
export function toApiFilters(f: Filters): ListingFilters {
  return {
    search: f.search,
    priceMin: f.priceMin,
    priceMax: f.priceMax,
    bed: f.bed,
    bath: f.bath,
    propertyType: f.types,
    mlsstatus: f.status,
  };
}

// How many filters differ from the defaults (drives the reset affordance and
// the mobile "Filters" badge count).
export function activeFilterCount(f: Filters): number {
  let n = 0;
  if (f.search.trim()) n++;
  if (f.priceMin || f.priceMax) n++;
  if (f.bed) n++;
  if (f.bath) n++;
  if (f.types.length) n++;
  if (f.status !== DEFAULT_FILTERS.status) n++;
  return n;
}

// Serialize/deserialize filters to a URLSearchParams for shareable links. Only
// non-default values are written so URLs stay clean.
export function filtersToParams(f: Filters, params: URLSearchParams): void {
  if (f.search.trim()) params.set("q", f.search.trim());
  else params.delete("q");
  if (f.priceMin) params.set("min", f.priceMin);
  else params.delete("min");
  if (f.priceMax) params.set("max", f.priceMax);
  else params.delete("max");
  if (f.bed) params.set("bed", f.bed);
  else params.delete("bed");
  if (f.bath) params.set("bath", f.bath);
  else params.delete("bath");
  if (f.types.length) params.set("type", f.types.join(","));
  else params.delete("type");
  if (f.status && f.status !== DEFAULT_FILTERS.status)
    params.set("status", f.status);
  else params.delete("status");
}

export function filtersFromParams(params: URLSearchParams): Filters {
  const type = params.get("type");
  return {
    search: params.get("q") ?? "",
    priceMin: params.get("min") ?? "",
    priceMax: params.get("max") ?? "",
    bed: params.get("bed") ?? "",
    bath: params.get("bath") ?? "",
    types: type ? type.split(",").filter(Boolean) : [],
    status: params.get("status") ?? DEFAULT_FILTERS.status,
  };
}
